import type { ScreenSchema } from './types';
import questionSchema from './screen-question.schema';
import tabsSchema from './screen-tabs.schema';
import accordionSchema from './screen-accordion.schema';
import carouselSchema from './screen-carousel.schema';
import blindsSchema from './screen-blinds.schema';
import flipCardsSchema from './screen-flip-cards.schema';
import orderingQuestionSchema from './screen-ordering-question.schema';
import gridQuestionSchema from './screen-grid-question.schema';
import likertQuestionSchema from './screen-likert-question.schema';
import sliderSchema from './screen-slider.schema';
// Phase 5 — Intro-only and simple templates
import cardsSchema from './screen-cards.schema';
import chatSchema from './screen-chat.schema';
import unitTitleSchema from './screen-unit-title.schema';
import unitEndSchema from './screen-unit-end.schema';
import textareaSchema from './screen-textarea.schema';
import builderSchema from './screen-builder.schema';
import lightboxSchema from './screen-lightbox.schema';
import graphSchema from './screen-graph.schema';
import passwordMeterSchema from './screen-password-meter.schema';
import dashboardSchema from './screen-dashboard.schema';
import toggleSchema from './screen-toggle.schema';
import dragSchema from './screen-drag.schema';
import hotspotSchema from './screen-hotspot.schema';
import courseTitleSchema from './screen-course-title.schema';
import esgTitleSchema from './screen-esg-title.schema';
import timelineSchema from './screen-timeline.schema';
// Phase 7 — Default content schema and remaining display templates
import contentSchema from './screen-content.schema';
import imageListSchema from './screen-image-list.schema';
import bigQuoteSchema from './screen-big-quote.schema';
import hotspotFullscreenSchema from './screen-hotspot-fullscreen.schema';
import hotspotGameSchema from './screen-hotspot-game.schema';
import stickySchema from './screen-sticky.schema';
import splitSchema from './screen-split.schema';
import slidesSchema from './screen-slides.schema';
// Phase 7c — email and voicemail templates
import emailSchema from './screen-email.schema';
import emailQuestionSchema from './screen-email-question.schema';
import voicemailSchema from './screen-voicemail.schema';
import voicemailQuestionSchema from './screen-voicemail-question.schema';
// Phase 7d — assessment template + quiz/bank/result container schemas
import assessmentSchema from './screen-assessment.schema';
import quizSchema from './screen-quiz.schema';
import bankSchema from './screen-bank.schema';
import resultSchema from './screen-result.schema';

/**
 * Registry mapping template names to their schema definitions.
 * Schemas are registered incrementally as templates are migrated.
 */
const registry = new Map<string, ScreenSchema>();

/** Register a schema, mapping each of its template names into the registry. */
export function registerSchema(schema: ScreenSchema): void {
  const templates = Array.isArray(schema.template)
    ? schema.template
    : [schema.template];
  templates.forEach(t => registry.set(t, schema));
}

// Register all migrated schemas
registerSchema(questionSchema);
registerSchema(tabsSchema);
registerSchema(accordionSchema);
registerSchema(carouselSchema);
registerSchema(blindsSchema);
registerSchema(flipCardsSchema);
registerSchema(orderingQuestionSchema);
registerSchema(gridQuestionSchema);
registerSchema(likertQuestionSchema);
registerSchema(sliderSchema);
// Phase 5
registerSchema(cardsSchema);
registerSchema(chatSchema);
registerSchema(unitTitleSchema);
registerSchema(unitEndSchema);
registerSchema(textareaSchema);
registerSchema(builderSchema);
registerSchema(lightboxSchema);
registerSchema(graphSchema);
registerSchema(passwordMeterSchema);
registerSchema(dashboardSchema);
registerSchema(toggleSchema);
registerSchema(dragSchema);
registerSchema(hotspotSchema);
registerSchema(courseTitleSchema);
registerSchema(esgTitleSchema);
// Phase 6
registerSchema(timelineSchema);
// Phase 7 — content-only display templates and corrected per-template schemas
registerSchema(contentSchema);
registerSchema(imageListSchema);
registerSchema(bigQuoteSchema);
registerSchema(hotspotFullscreenSchema);
registerSchema(hotspotGameSchema);
// Phase 7b — container templates
registerSchema(stickySchema);
registerSchema(slidesSchema);
registerSchema(splitSchema);
// Phase 7c — email and voicemail templates
registerSchema(emailSchema);
registerSchema(emailQuestionSchema);
registerSchema(voicemailSchema);
registerSchema(voicemailQuestionSchema);
// Phase 7d — assessment template + quiz/bank/result container schemas
registerSchema(assessmentSchema);
registerSchema(quizSchema);
registerSchema(bankSchema);
registerSchema(resultSchema);

/**
 * Look up the schema for a given template name.
 *
 * - When `template` is absent/null (screens with no template set, rendered by
 *   the v-else default in uc-screen) → returns the generic content schema so
 *   JiT audio works for plain heading/text/instructions screens.
 * - When `template` is a known string but not yet registered → returns null
 *   (pre-migration state; these screens continue to use S3 audio via
 *   uc-scroll-section's computedAudio fallback).
 */
export function schemaForTemplate(
  template?: string | null
): ScreenSchema | null {
  if (!template) return contentSchema;
  return registry.get(template) ?? null;
}
